/**
 * SherpaProjectTemplateDetailsShell - Shell component for Sherpa project template details
 *
 * This component handles all API logic for the Sherpa project template details page.
 * It wraps ProjectTemplateDetailsUI and provides Sherpa-specific API calls.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { deleteProjectTemplate, getProjectTemplate, putBulkProjectTemplateTdm } from '@/api/Sherpa/ProjectTemplate';
import { SherpaProjectReactQueryKey } from '@/constants/ReactQueryKeys';
import { usePermission } from '@/hooks/usePermission';
import { AppRoutes, buildRoute } from '@/route/routeConfig';
import { notifyUser } from '@/store/Toast';
import { sanitizeResponseError } from '@/utils';
import ProjectTemplateDeliverablesTable from '../../Components/ProjectTemplateDetails/ProjectTemplateDeliverablesTable';
import ProjectTemplateMilestonesTable from '../../Components/ProjectTemplateDetails/ProjectTemplateMilestonesTable';
import ProjectTemplateTasksTable from '../../Components/ProjectTemplateDetails/ProjectTemplateTasksTable';
import { ProjectTemplateDetailsUI } from '../../Components/UI/ProjectTemplateDetailsUI';
import type { ProjectTemplateData } from '../../Components/UI/ProjectTemplateDetailsUI/types';

// Enums matching backend constants
enum TemplateTypes {
  TASK = 'TASK',
  MILESTONE = 'MILESTONE',
  DELIVERABLE = 'DELIVERABLE',
}

enum TaskPhases {
  ACQUISITION = 'ACQUISITION',
  PROCUREMENT = 'PROCUREMENT',
  DEVELOPMENT = 'DEVELOPMENT',
  DESIGN = 'DESIGN',
  CONSTRUCTION = 'CONSTRUCTION',
  FINANCE = 'FINANCE',
}

// CSV column mapping
const CSV_COLUMN_MAP = {
  phase: 'Phase',
  referenceType: 'Reference Type',
  category: 'Category',
  item: 'Item',
  lead: 'Lead',
  support: 'Support',
  vendor: 'Vender',
};

// Reference type mapping from CSV to TemplateTypes
const REFERENCE_TYPE_MAP: Record<string, TemplateTypes> = {
  Task: TemplateTypes.TASK,
  Deliverables: TemplateTypes.DELIVERABLE,
  Deliverable: TemplateTypes.DELIVERABLE,
  Milestone: TemplateTypes.MILESTONE,
  Milestones: TemplateTypes.MILESTONE,
};

// Phase mapping from CSV to TaskPhases
const PHASE_MAP: Record<string, TaskPhases> = {
  Acquisition: TaskPhases.ACQUISITION,
  Finance: TaskPhases.FINANCE,
  Procurement: TaskPhases.PROCUREMENT,
  Development: TaskPhases.DEVELOPMENT,
  Design: TaskPhases.DESIGN,
  Construction: TaskPhases.CONSTRUCTION,
};

export const SherpaProjectTemplateDetailsShell = () => {
  const { templateId } = useParams();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { hasPermission } = usePermission();

  // Fetch project template
  const fetchProjectTemplate = useQuery({
    queryKey: [SherpaProjectReactQueryKey.GET_PROJECT_TEMPLATE, templateId],
    queryFn: () => getProjectTemplate({ templateId: Number(templateId) }),
  });
  const projectTemplate = fetchProjectTemplate?.data?.data;

  // Transform parsed CSV data to the required schema
  const transformCSVData = useCallback((parsedData: Record<string, any>[]) => {
    const taskItems: any[] = [];
    const milestoneItems: any[] = [];
    const deliverableItems: any[] = [];

    parsedData.forEach((row) => {
      const referenceType = row[CSV_COLUMN_MAP.referenceType];
      const templateType = REFERENCE_TYPE_MAP[referenceType];
      const phaseValue = row[CSV_COLUMN_MAP.phase];
      const phase = PHASE_MAP[phaseValue];

      if (!templateType || !phase) {
        console.warn('Invalid reference type or phase:', {
          referenceType,
          phaseValue,
        });
        return;
      }

      const lead = row[CSV_COLUMN_MAP.lead]?.trim() || '';
      const support = row[CSV_COLUMN_MAP.support]?.trim() || '';
      const category = row[CSV_COLUMN_MAP.category]?.trim() || '';
      const vendor = row[CSV_COLUMN_MAP.vendor]?.trim() || '';

      switch (templateType) {
        case TemplateTypes.TASK:
          taskItems.push({
            task: row[CSV_COLUMN_MAP.item] || '',
            category,
            vendor,
            phase,
            lead,
            support,
            note: '',
            templateFile: [],
          });
          break;

        case TemplateTypes.MILESTONE:
          milestoneItems.push({
            milestone: row[CSV_COLUMN_MAP.item] || '',
            phase,
            lead,
            support,
            note: '',
            templateFile: [],
          });
          break;

        case TemplateTypes.DELIVERABLE:
          deliverableItems.push({
            deliverable: row[CSV_COLUMN_MAP.item] || '',
            phase,
            lead,
            support,
            note: '',
            templateFile: [],
          });
          break;
      }
    });

    const result: any[] = [];

    if (taskItems.length > 0) {
      result.push({
        resourceType: TemplateTypes.TASK,
        templateTdms: taskItems,
      });
    }

    if (milestoneItems.length > 0) {
      result.push({
        resourceType: TemplateTypes.MILESTONE,
        templateTdms: milestoneItems,
      });
    }

    if (deliverableItems.length > 0) {
      result.push({
        resourceType: TemplateTypes.DELIVERABLE,
        templateTdms: deliverableItems,
      });
    }

    return result;
  }, []);

  // Mutation for bulk TDM import from CSV
  const bulkImportTdm = useMutation(
    (data: { templateId: number; payload: any[] }) =>
      putBulkProjectTemplateTdm({
        templateId: data.templateId,
        data: data.payload,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [SherpaProjectReactQueryKey.GET_PROJECT_TEMPLATE, templateId] });
        queryClient.invalidateQueries({ queryKey: ['get-project-template-tdm'] });
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'CSV data imported successfully.',
            toastType: 'success',
          })
        );
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const handleCSVUpload = (parsedData: Record<string, any>[]) => {
    const transformedData = transformCSVData(parsedData);
    bulkImportTdm.mutate({
      templateId: Number(templateId),
      payload: transformedData,
    });
  };

  // Delete mutation
  const removeProjectTemplate = useMutation((data: { templateId: number }) => deleteProjectTemplate({ templateId: data.templateId }), {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SherpaProjectReactQueryKey.GET_PROJECT_TEMPLATES] });
      navigate(buildRoute(AppRoutes.SHERPA_PROJECT_TEMPLATES), { replace: true });
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Project Template deleted successfully',
          toastType: 'success',
        })
      );
    },
    onError: (error: any) => {
      const resError = sanitizeResponseError(error);
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: resError,
          toastType: 'failed',
        })
      );
    },
  });

  const handleDelete = () => {
    if (projectTemplate?.id) {
      removeProjectTemplate.mutate({
        templateId: Number(projectTemplate.id),
      });
    }
  };

  const handleEdit = (_data: Partial<ProjectTemplateData>) => {
    // Edit is handled by the EditProjectTemplate component inside the UI
    // This is just a placeholder for future refactoring
  };

  // Render TDM tables using existing Sherpa-specific components
  const renderTasksTable = (activeNav: { name: string; slug: string }) => <ProjectTemplateTasksTable activeNav={activeNav} />;

  const renderDeliverablesTable = (activeNav: { name: string; slug: string }) => <ProjectTemplateDeliverablesTable activeNav={activeNav} />;

  const renderMilestonesTable = (activeNav: { name: string; slug: string }) => <ProjectTemplateMilestonesTable activeNav={activeNav} />;

  return (
    <ProjectTemplateDetailsUI
      template={projectTemplate as ProjectTemplateData}
      isLoading={fetchProjectTemplate.isLoading}
      onEdit={handleEdit}
      onDelete={handleDelete}
      onCSVUpload={handleCSVUpload}
      isDeleting={removeProjectTemplate.isLoading}
      isCSVUploading={bulkImportTdm.isLoading}
      hasPermission={hasPermission}
      renderTasksTable={renderTasksTable}
      renderDeliverablesTable={renderDeliverablesTable}
      renderMilestonesTable={renderMilestonesTable}
    />
  );
};

export default SherpaProjectTemplateDetailsShell;
